import React, { useEffect, useState } from "react";
import { FileText, Trash2, Edit } from "lucide-react";
import { useDispatch } from "react-redux";
import {
  PatientReportApi,
  Report,
} from "../../../../../interfaces/screens/content/patientDetails";
import { fetchReports, updatePatientReport } from "../actions";
import {
  showModal,
  hideModal,
} from "../../../../../connected-components/modal/reducer";
import { ModalType } from "../../../../../shared/utils/enums";
import reports from "../../../../../api/reports";
import forms from "../../../../../api/forms";
import { toastService } from "../../../../../shared/utils/toast";
import MainTable from "../../../../../components/mainTable";
import { TableColumnsItem } from "../../../../../interfaces/components/mainTable";
import { ColumnType, ButtonType } from "../../../../../shared/utils/enums";
import {
  showLoading,
  hideLoading,
} from "../../../../../connected-components/loader/reducer";
import DynamicFormRenderer from "../../../../../components/dynamicFormRenderer";

interface ReportsTabProps {
  patientId: number;
  refreshKey?: number;
  onAddReport: () => void;
}

export const ReportsTab: React.FC<ReportsTabProps> = ({
  patientId,
  refreshKey,
  onAddReport,
}) => {
  const [reportsList, setReportsList] = useState<Report[]>([]);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();

  const loadReports = async () => {
    setLoading(true);
    const res = await fetchReports(patientId);
    if (
      res &&
      res.result === "success" &&
      res.content &&
      Array.isArray(res.content.data)
    ) {
      setReportsList(
        res.content.data.map((rep: PatientReportApi) => ({
          id: String(rep.id),
          form_id: rep.form_id,
          form_label: rep.form_label,
          form_type: rep.form_type,
          created_at: rep.created_at,
        })),
      );
    } else {
      setReportsList([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, refreshKey]);

  const handleDelete = (reportId: string) => {
    dispatch(
      showModal({
        type: ModalType.QUESTION,
        title: "Delete Report",
        message:
          "Are you sure you want to delete this report? This action cannot be undone.",
        onConfirm: async () => {
          try {
            dispatch(showLoading());
            await reports.deleteReport(reportId);
            toastService.showSuccessToast("Report deleted successfully");
            setReportsList((prev) => prev.filter((r) => r.id !== reportId));
          } catch (err) {
            toastService.showErrorToast("Failed to delete report");
          } finally {
            dispatch(hideLoading());
          }
        },
        onCancel: () => {},
      }),
    );
  };

  const handleEdit = async (report: Report) => {
    try {
      dispatch(showLoading());
      const formResponse = await forms.getForm(report.id);
      if (formResponse.result === "success" && formResponse.content) {
        let formFields = formResponse.content?.form_fields?.map(
          (item: any) => item?.form_field,
        );
        const initialValue = formResponse.content?.form_fields?.reduce(
          (acc: any, item: any) => {
            const key = item.form_field.id;
            const value = item.value || "";
            acc[key] = value;
            return acc;
          },
          {},
        );
        dispatch(
          showModal({
            type: ModalType.COMPONENT,
            title: `Edit ${formResponse.content.form_label}`,
            component: (
              <DynamicFormRenderer
                fields={formFields as any}
                acceptExtraFields={
                  formResponse.content.form.accept_extra_fields
                }
                initExtraFields={formResponse.content.extra_fields || []}
                initialValues={initialValue}
                gridConfig={{
                  mobile: 1,
                  desktop: formFields.length > 1 ? 3 : 1,
                }}
                onSubmit={async (values) => {
                  try {
                    dispatch(showLoading());
                    const temp = { ...values };
                    delete temp["extra_fields"];
                    const form = {
                      form_id: report.form_id,
                      extra_fields: values.extra_fields,
                      values: temp,
                    };
                    const success = await updatePatientReport(report.id, form);
                    if (success) {
                      toastService.showSuccessToast(
                        "Report updated successfully",
                      );
                      loadReports();
                      dispatch(hideModal());
                    }
                  } catch (err) {
                    toastService.showErrorToast("Failed to update report");
                  } finally {
                    dispatch(hideLoading());
                  }
                }}
              />
            ),
          }),
        );
      } else {
        toastService.showErrorToast("Failed to load form fields");
      }
    } catch (err) {
      toastService.showErrorToast("Failed to load form fields");
    } finally {
      dispatch(hideLoading());
    }
  };

  const columns: TableColumnsItem[] = [
    {
      key: "form_label",
      name: "Report Name",
      type: ColumnType.CUSTOM,
      customValue: (row: Report) => (
        <div className="flex items-center">
          <FileText className="h-5 w-5 text-gray-400 mr-2" />
          <div className="text-sm font-medium text-gray-900 dark:text-white">
            {row.form_label}
          </div>
        </div>
      ),
    },
    {
      key: "form_type",
      name: "Type",
      type: ColumnType.TEXT,
    },
    {
      key: "created_at",
      name: "Date",
      type: ColumnType.CUSTOM,
      customValue: (row: Report) =>
        new Date(row.created_at).toLocaleDateString(),
    },
    {
      key: "actions",
      name: "Actions",
      type: ColumnType.BUTTON,
      buttonsList: [
        {
          type: ButtonType.ICON,
          icon: Edit,
          cssClass: "edit",
          onPress: (_: React.MouseEvent, row: Report) => handleEdit(row),
        },
        {
          type: ButtonType.ICON,
          icon: Trash2,
          cssClass: "delete",
          onPress: (_: React.MouseEvent, row: Report) => handleDelete(row.id),
        },
      ],
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-medium dark:text-white">Patient Reports</h2>
      </div>

      <MainTable
        data={reportsList}
        columns={columns}
        isLoading={loading}
        showSearch={true}
        showAddButton={false}
        addButtonLabel="Add Report"
        onAddNewItemPress={onAddReport}
        showPagination={false}
        emptyListHeight="30vh"
        onRowPress={handleEdit}
      />
    </div>
  );
};
